import React from "react";
import { styled } from "@mui/material/styles";
import { AppBar, Box, Container, Fab, Tab, Tabs } from "@mui/material";
import { searchService, zoneService } from "../../services";
import TransferList from "../../components/utils/TransferList";
import UploadForm from "../../components/forms/UploadForm";
import { Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import Loading from "../../components/utils/Loading";
import { useTranslation } from "react-i18next";
import { normalizeText } from "../../helpers";
import {
  NavigationType,
  useNavigationType,
  useNavigate,
} from "react-router-dom";
import ActionComponent from "../../components/utils/ActionComponent";

const PREFIX = "Upload";

const classes = {
  root: `${PREFIX}-root`,
  fab: `${PREFIX}-fab`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    position: "relative",
    margin: theme.spacing(1),
    paddingBottom: 50,
    backgroundColor: "#424242",
    marginTop: "1rem",
  },

  [`& .${classes.fab}`]: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(3),
    [theme.breakpoints.between("xs", "md")]: {
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `wrapped-tab-${index}`,
    "aria-controls": `wrapped-tabpanel-${index}`,
  };
}

export default function Upload() {
  const { t } = useTranslation();
  const history = useNavigate();
  const navigationType = useNavigationType();
  const [state, setState] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    setState({
      value: "one",
      searchValue: "",
      results: [],
      zones: null,
      file: null,
      link: null,
      saving: false,
      infos: { title: "", description: "" },
    });
  }, []);

  const handleChange = (event, newValue) => {
    setState({ ...state, value: newValue });
  };

  function search(value, offset, limit) {
    setState({ ...state, searchValue: value });
    searchService
      .searchZones(value || "", offset || 0, limit || 20)
      .then((data) => {
        setState({ ...state, results: data, searchValue: value });
      });
  }

  function searchNext(offset, limit) {
    searchService
      .searchZones(state?.searchValue || "", offset || 0, limit || 20)
      .then((data) => {
        setState({
          ...state,
          results: {
            ...state.results,
            ...data,
            data: [...(state?.results?.data || []), ...(data.data || [])],
          },
        });
      });
  }

  function saveFile(file) {
    setState({ ...state, file, link: null });
  }

  function saveLink(link) {
    setState({ ...state, link, file: null });
  }

  function saveTitle(title) {
    setState({ ...state, infos: { ...state.infos, title } });
  }

  function saveDescription(description) {
    setState({ ...state, infos: { ...state.infos, description } });
  }

  function saveZones(zones) {
    setState({ ...state, zones });
  }

  function save() {
    setSaving(true);
    let tmp = { ...state };
    tmp.infos.title = normalizeText(tmp.infos.title);
    tmp.infos.description = normalizeText(tmp.infos.description);
    zoneService
      .uploadMediaToZones(tmp)
      .then(() => {
        enqueueSnackbar(t("uploadMultipleZones.success"), {
          variant: "success",
        });
        setSaving(false);
        if (navigationType === NavigationType.POP) {
          history("/");
        } else {
          history(-1);
        }
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("uploadMultipleZones.error"), { variant: "error" });
        setSaving(false);
      });
  }

  return (
    <StyledDiv>
      <Container>
        {saving ? (
          <div className="loading-container">
            <Loading message={t("uploadMultipleZones.saving")} />
          </div>
        ) : (
          <div className={classes.root}>
            <AppBar position="static" color="transparent">
              <Tabs
                value={state.value || "one"}
                onChange={handleChange}
                aria-label="wrapped label tabs example"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab
                  value="one"
                  label={t("uploadMultipleZones.zones")}
                  wrapped
                  {...a11yProps("one")}
                />
                <Tab
                  value="two"
                  label={t("uploadMultipleZones.content")}
                  {...a11yProps("two")}
                />
              </Tabs>
            </AppBar>
            <ActionComponent
              icon={<Save />}
              action={save}
              disabled={
                !state.zones ||
                state.zones?.length <= 0 ||
                (!state.file && !state.link) ||
                state.infos.title.length === 0 ||
                state.infos.description.length === 0
              }
            />
            <TabPanel value={state.value} index="one">
              <TransferList
                search={search}
                results={state.results}
                save={saveZones}
                fetchNext={searchNext}
                checked={state.zones}
                searchValue={state.searchValue}
              />
            </TabPanel>
            <TabPanel value={state.value} index="two">
              <UploadForm
                save={saveFile}
                file={state.file}
                saveLink={saveLink}
                saveTitle={saveTitle}
                saveDescription={saveDescription}
              />
            </TabPanel>
            <Fab
              aria-label={"label"}
              className={classes.fab}
              color="primary"
              disabled={
                !state.zones ||
                state.zones?.length <= 0 ||
                (!state.file && !state.link) ||
                state.infos.title.length === 0 ||
                state.infos.description.length === 0
              }
            >
              <Save onClick={save} />
            </Fab>
          </div>
        )}
      </Container>
    </StyledDiv>
  );
}
